/**
 * Order history screen — a store's past/current orders across weeks, most
 * recent first. Read-only; each row links to the order's detail page.
 *
 * Mirrors ProposalPage's remount-on-store-switch pattern (see ProposalPage.tsx
 * for the full rationale): the outer component resolves activeStore and renders
 * Inner with key={activeStore.id}, so switching stores resets the pagination
 * offset owned by Inner instead of leaking one store's page position into
 * another's screen.
 */
import React, {useState} from 'react'
import {Link} from 'react-router-dom'
import {Pagination, Tag} from '../../components'
import {deriveOrderStatus} from '@retail/orders-domain'
import {useActiveStore} from '../../store/StoreContext'
import {storeCodeSegment} from '../../lib/storeRouting'
import {orderDetailPath} from '../../lib/routes'
import {useOrderHistory} from './useOrderHistory'
import {mapOrderError} from '../../lib/orderErrors'
import {STATUS_BG, STATUS_LABELS} from '../../lib/orderStatus'
import {DEFAULT_PAGE_LIMIT} from './orderHistoryViewModel'
import styles from './OrderHistoryPage.module.scss'

export const OrderHistoryPage = (): React.ReactElement => {
  const {activeStore} = useActiveStore()

  if (activeStore === undefined) {
    return (
      <div className={styles['order-history']}>
        <div className={`row-flx flx-a-c flx-j-c p-24 txt-a-c bg-white txt-light-kale ${styles['order-history__state']}`}>Cargando tienda…</div>
      </div>
    )
  }

  // key={activeStore.id}: remounts on store switch, resetting the pagination
  // offset — same cross-store-state fix as ProposalPage's key={storeId}.
  return (
    <OrderHistoryPageInner
      key={activeStore.id}
      storeId={activeStore.id}
      storeSeg={storeCodeSegment(activeStore)}
    />
  )
}

type OrderHistoryPageInnerProps = {
  storeId: number
  /** The active store's URL segment, so row links stay store-scoped. */
  storeSeg: string
}

const OrderHistoryPageInner = ({storeId, storeSeg}: OrderHistoryPageInnerProps): React.ReactElement => {
  const [offset, setOffset] = useState(0)
  const viewModel = useOrderHistory({storeId, limit: DEFAULT_PAGE_LIMIT, offset})

  const handlePrev = () => setOffset((prev) => Math.max(0, prev - viewModel.limit))
  const handleNext = () => setOffset((prev) => prev + viewModel.limit)

  return (
    <div className={styles['order-history']}>
      <div className={`m-v-0 m-h-auto ${styles['order-history__wrap']}`}>
        <div className={styles['order-history__ctx']}>
          <div className={`m-b-4 txt-bold-500 txt-upper txt-light-kale ${styles['order-history__overline']}`}>Todas las semanas</div>
          <h1 className={`m-h-0 m-b-0 txt-honey txt-kale ${styles['order-history__title']}`}>Pedidos</h1>
        </div>

        {viewModel.isLoading ? (
          <div className={`row-flx flx-a-c flx-j-c p-24 txt-a-c bg-white txt-light-kale ${styles['order-history__state']}`}>Cargando pedidos…</div>
        ) : viewModel.isError ? (
          <div className={`row-flx flx-a-c flx-j-c p-24 txt-a-c bg-white ${styles['order-history__state--error']}`}>{mapOrderError(viewModel.error, 'read')}</div>
        ) : viewModel.rows.length === 0 ? (
          <div className={`row-flx flx-a-c flx-j-c p-24 txt-a-c bg-white txt-light-kale ${styles['order-history__state']}`}>Aún no hay pedidos. Tu primera propuesta aparecerá aquí.</div>
        ) : (
          <>
            <div className={`bg-white ${styles['order-history__table']}`}>
              <div className={styles['order-history__thead']}>
                <span>Semana</span>
                <span>Estado</span>
                <span className={`txt-a-r ${styles['order-history__cell--right']}`}>Unidades</span>
                <span>Entrega</span>
              </div>
              {viewModel.rows.map((row) => (
                <Link
                  key={row.order.id}
                  to={orderDetailPath(storeSeg, row.order.id)}
                  className={`txt-kale txt-decoration-none ${styles['order-history__row']}`}
                >
                  <span className={`txt-bold-500 ${styles['order-history__week']}`}>{row.weekLabel}</span>
                  <span className={styles['order-history__status']}>
                    <Tag
                      label={STATUS_LABELS[deriveOrderStatus(row.order, new Date())]}
                      bgColor={STATUS_BG[deriveOrderStatus(row.order, new Date())]}
                    />
                  </span>
                  <span className={`txt-a-r ${styles['order-history__units']}`}>
                    {row.plates}
                    {/* Only visible once the row stacks and the header no longer
                        labels this number — see the tablet breakpoint. */}
                    <span className={styles['order-history__units-noun']}>
                      {` ${row.plates === 1 ? 'unidad' : 'unidades'}`}
                    </span>
                  </span>
                  <span className={`txt-light-kale ${styles['order-history__delivery']}`}>{row.deliveryLabel}</span>
                </Link>
              ))}
            </div>

            <div className="m-t-16">
              <Pagination
                offset={viewModel.offset}
                limit={viewModel.limit}
                total={viewModel.total}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
